import {FC, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Swal from 'sweetalert2'

type Booking = {
  id: number
  name: string
  people: number
  phone?: string
  email?: string
  bookingHourId: number
  date: string
  isAllocated?: boolean
  allocationId?: number
}

type Allocation = {
  id: number
  environmentTableId: number
  bookingId: number
  clientName: string
  people: number
  phone?: string
  email?: string
  status: string
}

type Table = {
  id: number
  tableNumber: string
  customCapacity?: number
  tableType?: {
    capacity: number
  }
}

type Props = {
  show: boolean
  onHide: () => void
  table: Table | null
  allocation: Allocation | null
  availableBookings: Booking[]
  availableTables?: Table[]
  allocations?: Allocation[]
  onAllocate: (tableId: number, bookingId: number) => Promise<void>
  onAllocateWalkIn?: (tableIds: number[], people: number, name: string, phone: string, email: string) => Promise<void>
  onDeallocate: (allocationId: number) => Promise<void>
  onChangeStatus?: (allocationId: number, newStatus: 'allocated' | 'occupied' | 'cleaning') => Promise<void>
}

export const QuickAllocationModal: FC<Props> = ({
  show,
  onHide,
  table,
  allocation,
  availableBookings,
  availableTables = [],
  allocations = [],
  onAllocate,
  onAllocateWalkIn,
  onDeallocate,
  onChangeStatus,
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null)
  const [showWalkInForm, setShowWalkInForm] = useState(false)
  const [walkInPeople, setWalkInPeople] = useState(2)
  const [walkInName, setWalkInName] = useState('')
  const [walkInPhone, setWalkInPhone] = useState('')
  const [walkInEmail, setWalkInEmail] = useState('')
  const [suggestedTables, setSuggestedTables] = useState<Table[]>([])

  // Limpar form ao fechar modal
  const handleClose = () => {
    setSelectedBookingId(null)
    setShowWalkInForm(false)
    setWalkInPeople(2)
    setWalkInName('')
    setWalkInPhone('')
    setWalkInEmail('')
    setSuggestedTables([])
    onHide()
  }

  const handleAllocate = async () => {
    if (!table || !selectedBookingId) return

    setLoading(true)
    try {
      await onAllocate(table.id, selectedBookingId)
      setSelectedBookingId(null)
      onHide()
    } catch (error) {
      console.error('Erro ao alocar reserva:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeallocate = async () => {
    if (!allocation) return

    setLoading(true)
    try {
      await onDeallocate(allocation.id)
      handleClose()
    } catch (error) {
      console.error('Erro ao desalocar reserva:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleWalkInAllocate = async () => {
    if (!table || !onAllocateWalkIn || !walkInName.trim()) {
      console.log('⚠️ Validação falhou:', { table: !!table, onAllocateWalkIn: !!onAllocateWalkIn, walkInName })
      return
    }

    const tableCapacity = table?.customCapacity || table?.tableType?.capacity || 0
    console.log('🎯 Tentando alocar walk-in:', { people: walkInPeople, capacity: tableCapacity })
    
    // Se cabe na mesa atual, aloca direto
    if (walkInPeople <= tableCapacity) {
      setLoading(true)
      try {
        console.log('✅ Alocando direto na mesa:', table.id)
        await onAllocateWalkIn([table.id], walkInPeople, walkInName, walkInPhone, walkInEmail)
        handleClose()
      } catch (error) {
        console.error('❌ Erro ao alocar walk-in:', error)
      } finally {
        setLoading(false)
      }
    } else {
      // Sugerir união de mesas inline (sem modal separado)
      console.log('🔍 Buscando combinação de mesas...')
      const combination = findBestTableCombination(walkInPeople, table, availableTables || [])
      console.log('📋 Combinação encontrada:', combination)
      
      if (combination.length > 0) {
        setSuggestedTables(combination)
        // NÃO abre modal separado, apenas atualiza o estado
        // O accordion será mostrado automaticamente quando suggestedTables.length > 1
      } else {
        console.error('❌ Não foi possível encontrar combinação de mesas')
        Swal.fire({
          icon: 'warning',
          title: 'Mesas Insuficientes',
          text: `Não há mesas disponíveis suficientes para acomodar ${walkInPeople} pessoas.`,
          confirmButtonText: 'Entendi',
        })
      }
    }
  }

  const handleConfirmCombination = async () => {
    if (!onAllocateWalkIn || suggestedTables.length === 0) return
    
    setLoading(true)
    try {
      const tableIds = suggestedTables.map(t => t.id)
      console.log('✅ Confirmando união de mesas:', tableIds)
      await onAllocateWalkIn(tableIds, walkInPeople, walkInName, walkInPhone, walkInEmail)
      handleClose()
    } catch (error) {
      console.error('❌ Erro ao confirmar união de mesas:', error)
    } finally {
      setLoading(false)
    }
  }

  // Recalcular sugestões quando mudar quantidade de pessoas
  const handlePeopleChange = (newPeople: number) => {
    setWalkInPeople(newPeople)
    
    // Se já tinha sugestão, recalcula
    if (suggestedTables.length > 0 && table) {
      console.log('🔄 Recalculando sugestão para', newPeople, 'pessoas')
      const newCombination = findBestTableCombination(newPeople, table, availableTables || [])
      setSuggestedTables(newCombination)
    }
  }

  // Algoritmo para encontrar melhor combinação de mesas
  const findBestTableCombination = (people: number, primaryTable: Table, tables: Table[]): Table[] => {
    console.log('🧮 Iniciando cálculo Best Fit para', people, 'pessoas')
    console.log('📋 Alocações existentes:', allocations.map(a => ({ tableId: a.environmentTableId, client: a.clientName })))
    
    // Filtrar apenas mesas DISPONÍVEIS (sem alocação)
    const occupiedTableIds = new Set(allocations.map(a => a.environmentTableId))
    console.log('🚫 Mesas ocupadas (IDs):', Array.from(occupiedTableIds))
    
    const availableTablesOnly = tables.filter(t => {
      const isOccupied = occupiedTableIds.has(t.id)
      const isDifferent = t.id !== primaryTable.id
      if (isOccupied) {
        console.log(`⛔ Mesa ${t.tableNumber} (ID: ${t.id}) está ocupada - IGNORADA`)
      }
      return isDifferent && !isOccupied
    })
    
    console.log('✅ Mesas disponíveis para união:', availableTablesOnly.map(t => `${t.tableNumber} (${t.customCapacity || t.tableType?.capacity || 0} lugares)`))
    
    const allTables = [primaryTable, ...availableTablesOnly].map(t => ({
      id: t.id,
      number: t.tableNumber,
      capacity: t.customCapacity || t.tableType?.capacity || 0,
      table: t
    }))
    
    console.log('🪑 Mesas disponíveis:', allTables.map(t => `${t.number} (${t.capacity})`).join(', '))
    
    type Combination = {
      tables: typeof allTables
      totalCapacity: number
      waste: number
      score: number
    }
    
    const combinations: Combination[] = []
    
    // Estratégia ajustada: prioriza menos desperdício quando significativo
    // Peso menor = favorece menos mesas, mas penaliza muito desperdício
    
    // 1. Tentar mesa única
    for (const t of allTables) {
      if (t.capacity >= people) {
        const waste = t.capacity - people
        // Penalizar desperdício maior que 50% da capacidade necessária
        const wastePenalty = waste > (people * 0.5) ? waste * 3 : waste
        const score = 100 + wastePenalty
        combinations.push({
          tables: [t],
          totalCapacity: t.capacity,
          waste,
          score
        })
        console.log(`  ✓ Mesa ${t.number} (${t.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
      }
    }
    
    // 2. Tentar combinações de 2 mesas
    for (let i = 0; i < allTables.length; i++) {
      for (let j = i + 1; j < allTables.length; j++) {
        const t1 = allTables[i]
        const t2 = allTables[j]
        const totalCapacity = t1.capacity + t2.capacity
        
        if (totalCapacity >= people) {
          const waste = totalCapacity - people
          const wastePenalty = waste > (people * 0.5) ? waste * 2 : waste
          const score = 150 + wastePenalty
          combinations.push({
            tables: [t1, t2],
            totalCapacity,
            waste,
            score
          })
          console.log(`  ✓ Mesas ${t1.number}+${t2.number} (${t1.capacity}+${t2.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
        }
      }
    }
    
    // 3. Tentar combinações de 3 mesas
    for (let i = 0; i < allTables.length; i++) {
      for (let j = i + 1; j < allTables.length; j++) {
        for (let k = j + 1; k < allTables.length; k++) {
          const t1 = allTables[i]
          const t2 = allTables[j]
          const t3 = allTables[k]
          const totalCapacity = t1.capacity + t2.capacity + t3.capacity
          
          if (totalCapacity >= people) {
            const waste = totalCapacity - people
            const wastePenalty = waste > (people * 0.5) ? waste * 2 : waste
            const score = 250 + wastePenalty
            combinations.push({
              tables: [t1, t2, t3],
              totalCapacity,
              waste,
              score
            })
            console.log(`  ✓ Mesas ${t1.number}+${t2.number}+${t3.number} (${t1.capacity}+${t2.capacity}+${t3.capacity}) - Desperdício: ${waste} - Penalidade: ${wastePenalty} - Score: ${score}`)
          }
        }
      }
    }
    
    // Ordenar por score (menor é melhor = Best Fit)
    combinations.sort((a, b) => a.score - b.score)
    
    if (combinations.length === 0) {
      console.log('❌ Nenhuma combinação encontrada')
      return []
    }
    
    const best = combinations[0]
    console.log(`⭐ MELHOR: ${best.tables.map(t => t.number).join('+')} (${best.totalCapacity} lugares) - Desperdício: ${best.waste} - Score: ${best.score}`)
    
    return best.tables.map(t => t.table)
  }

  const tableCapacity = table?.customCapacity || table?.tableType?.capacity || 0

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {allocation ? (
            <>
              <i className="bi bi-gear-fill me-2 text-info"></i>
              Gerenciar Mesa {table?.tableNumber}
            </>
          ) : (
            <>
              <i className="bi bi-lightning-charge me-2 text-primary"></i>
              Alocação Rápida - Mesa {table?.tableNumber}
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {allocation ? (
          // Modal de Desalocação
          <div>
            <div className="alert alert-warning d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
              <div>
                <strong>Atenção!</strong> Ao desalocar esta mesa, a reserva retornará para a lista
                de distribuição e poderá ser alocada em outra mesa.
              </div>
            </div>

            <div className="card border-warning">
              <div className="card-body">
                <h6 className="card-title text-dark fw-bold mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  Informações da Reserva Atual
                </h6>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small">Cliente</label>
                    <p className="fw-bold mb-0">
                      <i className="bi bi-person-fill me-2 text-primary"></i>
                      {allocation.clientName}
                    </p>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label text-muted small">Pessoas</label>
                    <p className="fw-bold mb-0">
                      <i className="bi bi-people-fill me-2 text-success"></i>
                      {allocation.people} pessoa(s)
                    </p>
                  </div>

                  {allocation.phone && (
                    <div className="col-md-6">
                      <label className="form-label text-muted small">Telefone</label>
                      <p className="fw-bold mb-0">
                        <i className="bi bi-telephone-fill me-2 text-info"></i>
                        {allocation.phone}
                      </p>
                    </div>
                  )}

                  {allocation.email && (
                    <div className="col-md-6">
                      <label className="form-label text-muted small">E-mail</label>
                      <p className="fw-bold mb-0 text-break">
                        <i className="bi bi-envelope-fill me-2 text-secondary"></i>
                        {allocation.email}
                      </p>
                    </div>
                  )}
                  
                  <div className="col-12">
                    <label className="form-label text-muted small">Status Atual</label>
                    <p className="fw-bold mb-0">
                      <i className={`bi ${
                        allocation.status === 'occupied' ? 'bi-person-check-fill me-2' :
                        allocation.status === 'cleaning' ? 'bi-trash-fill me-2' :
                        'bi-check-circle-fill me-2'
                      }`} style={{color: 
                        allocation.status === 'occupied' ? '#fd397a' :
                        allocation.status === 'cleaning' ? '#009ef7' :
                        '#50cd89'
                      }}></i>
                      {allocation.status === 'occupied' ? 'Ocupada' :
                       allocation.status === 'cleaning' ? 'Em Limpeza' :
                       'Alocada'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Modal de Alocação
          <div>
            <div className="alert alert-info d-flex align-items-center mb-3">
              <i className="bi bi-info-circle-fill fs-4 me-3"></i>
              <div>
                <strong>Mesa {table?.tableNumber}</strong> - Capacidade: {tableCapacity} pessoa(s)
                <br />
                <small className="text-muted">
                  {showWalkInForm
                    ? 'Preencha os dados do cliente walk-in'
                    : 'Selecione uma reserva abaixo para alocar nesta mesa'}
                </small>
              </div>
            </div>

            {/* Toggle entre Reserva e Walk-in */}
            <div className="d-flex gap-2 mb-3">
              <button
                className={`btn ${!showWalkInForm ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setShowWalkInForm(false)}
              >
                <i className="bi bi-calendar-check me-2"></i>
                Com Reserva
              </button>
              <button
                className={`btn ${showWalkInForm ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setShowWalkInForm(true)}
              >
                <i className="bi bi-person-plus me-2"></i>
                Walk-in (Sem Reserva)
              </button>
            </div>

            {/* Formulário Walk-in */}
            {showWalkInForm && (
              <div className="card border-success mb-3">
                <div className="card-body">
                  <h6 className="card-title text-success mb-3">
                    <i className="bi bi-person-plus-fill me-2"></i>
                    Alocação Walk-in
                  </h6>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Nome do Cliente <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={walkInName}
                      onChange={(e) => setWalkInName(e.target.value)}
                      placeholder="Digite o nome do cliente..."
                      disabled={loading}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Telefone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        value={walkInPhone}
                        onChange={(e) => setWalkInPhone(e.target.value)}
                        placeholder="(00) 00000-0000"
                        disabled={loading}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">E-mail</label>
                      <input
                        type="email"
                        className="form-control"
                        value={walkInEmail}
                        onChange={(e) => setWalkInEmail(e.target.value)}
                        placeholder="email@exemplo.com"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Número de Pessoas <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handlePeopleChange(Math.max(1, walkInPeople - 1))}
                        disabled={loading || walkInPeople <= 1}
                      >
                        <i className="bi bi-dash"></i>
                      </button>
                      <input
                        type="number"
                        className="form-control text-center"
                        min={1}
                        max={20}
                        value={walkInPeople}
                        onChange={(e) => {
                          const val = Number(e.target.value)
                          if (val >= 1 && val <= 20) handlePeopleChange(val)
                        }}
                        disabled={loading}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => handlePeopleChange(Math.min(20, walkInPeople + 1))}
                        disabled={loading || walkInPeople >= 20}
                      >
                        <i className="bi bi-plus"></i>
                      </button>
                    </div>
                    <small className="text-muted">Capacidade da mesa: {tableCapacity}</small>
                  </div>

                  {walkInPeople > tableCapacity && (
                    <div className="alert alert-warning mb-3">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Atenção:</strong> O número de pessoas ({walkInPeople}) excede a capacidade da mesa ({tableCapacity}).
                      Será necessário unir mesas.
                    </div>
                  )}

                  {/* Accordion de União de Mesas - Mostra sempre que houver sugestão */}
                  {suggestedTables.length > 0 && (
                    <div className="accordion mb-3" id="accordionCombination">
                      <div className="accordion-item border-warning">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button bg-warning bg-opacity-10"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseCombination"
                            aria-expanded="true"
                          >
                            <i className="bi bi-diagram-3 me-2 text-warning"></i>
                            <strong>União de Mesas Necessária</strong>
                          </button>
                        </h2>
                        <div
                          id="collapseCombination"
                          className="accordion-collapse collapse show"
                          data-bs-parent="#accordionCombination"
                        >
                          <div className="accordion-body p-2">
                            {/* Resumo da combinação */}
                            <div className="bg-primary bg-opacity-10 border border-primary rounded p-2 mb-2 d-flex align-items-center">
                              <i className="bi bi-diagram-3-fill text-primary fs-6 me-2"></i>
                              <div className="flex-grow-1">
                                <strong className="small d-block mb-0 text-primary">Melhor Combinação:</strong>
                                <div className="text-dark small lh-1">
                                  {suggestedTables.map((t, index) => {
                                    const capacity = t.customCapacity || t.tableType?.capacity || 0
                                    return (
                                      <span key={t.id}>
                                        {index > 0 && (
                                          <span className="text-muted mx-1">
                                            <i className="bi bi-plus-lg"></i>
                                          </span>
                                        )}
                                        <strong>Mesa {t.tableNumber}</strong>
                                        <span className="text-muted"> ({capacity} {capacity === 1 ? 'lugar' : 'lugares'})</span>
                                      </span>
                                    )
                                  })}
                                  {' '}
                                  <span className="text-success fw-bold">
                                    = {suggestedTables.reduce((sum, t) => sum + (t.customCapacity || t.tableType?.capacity || 0), 0)} lugares
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Lista de mesas */}
                            <div className="list-group list-group-flush mb-2">
                              {suggestedTables.map((t, index) => {
                                const capacity = t.customCapacity || t.tableType?.capacity || 0
                                return (
                                  <div
                                    key={t.id}
                                    className="list-group-item d-flex justify-content-between align-items-center py-2 px-2"
                                  >
                                    <div className="d-flex align-items-center">
                                      {index === 0 && (
                                        <span className="badge bg-primary me-2 small">Principal</span>
                                      )}
                                      <div>
                                        <strong className="d-block small">Mesa {t.tableNumber}</strong>
                                        <small className="text-muted">
                                          <i className="bi bi-people me-1"></i>
                                          {capacity} pessoa(s)
                                        </small>
                                      </div>
                                    </div>
                                    <i className="bi bi-check-circle-fill text-success"></i>
                                  </div>
                                )
                              })}
                            </div>

                            <div className="alert alert-info mb-0 py-2 small">
                              <i className="bi bi-info-circle me-2"></i>
                              As mesas ficarão <strong>vinculadas</strong> até a desalocação.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    className="btn btn-success w-100"
                    onClick={suggestedTables.length > 0 ? handleConfirmCombination : handleWalkInAllocate}
                    disabled={!walkInName.trim() || !walkInPhone.trim() || loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Alocando...
                      </>
                    ) : suggestedTables.length > 0 ? (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        {suggestedTables.length > 1 ? 'Confirmar União de Mesas' : 'Confirmar Alocação'}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Alocar Walk-in
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Lista de Reservas */}
            {!showWalkInForm && (
              availableBookings.length === 0 ? (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  Não há reservas disponíveis para alocação no filtro atual.
                </div>
              ) : (
                <div className="list-group" style={{maxHeight: '400px', overflowY: 'auto'}}>
                  {availableBookings.map((booking) => {
                    const isOverCapacity = booking.people > tableCapacity
                    const isSelected = selectedBookingId === booking.id

                    return (
                      <button
                        key={booking.id}
                        type="button"
                        className={`list-group-item list-group-item-action ${
                          isSelected ? 'active' : ''
                        } ${isOverCapacity ? 'border-warning' : ''}`}
                        onClick={() => setSelectedBookingId(booking.id)}
                        disabled={loading}
                      >
                        <div className="d-flex w-100 justify-content-between align-items-center">
                          <div>
                            <h6 className={`mb-1 ${isSelected ? 'text-white' : 'text-dark'}`}>
                              <i className="bi bi-person-circle me-2"></i>
                              {booking.name}
                            </h6>
                            <p className={`mb-1 small ${isSelected ? 'text-white-50' : 'text-muted'}`}>
                              <i className="bi bi-people me-1"></i>
                              {booking.people} pessoa(s)
                              {booking.phone && (
                                <>
                                  {' | '}
                                  <i className="bi bi-telephone me-1"></i>
                                  {booking.phone}
                                </>
                              )}
                            </p>
                          </div>
                          {isOverCapacity && (
                            <span className="badge bg-warning text-dark">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Acima da capacidade
                            </span>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {allocation ? (
          <div className="d-flex gap-2 w-100 justify-content-end align-items-center">
            {allocation.status !== 'occupied' && (
              <button
                type="button"
                className="btn btn-sm"
                style={{backgroundColor: '#fd397a', borderColor: '#fd397a', color: 'white'}}
                onClick={async () => {
                  const result = await Swal.fire({
                    title: 'Marcar como Ocupada?',
                    text: 'O cliente chegou e está sentado na mesa.',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Cliente Chegou',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#fd397a'
                  })
                  if (result.isConfirmed && onChangeStatus) {
                    await onChangeStatus(allocation.id, 'occupied')
                    handleClose()
                  }
                }}
                disabled={loading}
              >
                <i className="bi bi-person-check-fill me-1"></i>
                Marcar Ocupada
              </button>
            )}
            
            {allocation.status !== 'cleaning' && (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={async () => {
                  const result = await Swal.fire({
                    title: 'Iniciar Limpeza?',
                    text: 'A mesa será marcada como "Em Limpeza".',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, Iniciar Limpeza',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#0d6efd'
                  })
                  if (result.isConfirmed && onChangeStatus) {
                    await onChangeStatus(allocation.id, 'cleaning')
                    handleClose()
                  }
                }}
                disabled={loading}
              >
                <i className="bi bi-trash-fill me-1"></i>
                Iniciar Limpeza
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleDeallocate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Desalocando...
                </>
              ) : (
                <>
                  <i className="bi bi-unlock me-1"></i>
                  Desalocar Mesa
                </>
              )}
            </button>
            
            <button
              type="button"
              className="btn btn-dark btn-sm"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
          </div>
        ) : (
          <div className="d-flex gap-2 w-100 justify-content-end align-items-center">
            {!showWalkInForm && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAllocate}
                disabled={loading || !selectedBookingId}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Alocando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Alocar Reserva
                  </>
                )}
              </button>
            )}
            
            <button
              type="button"
              className="btn btn-dark btn-sm"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="bi bi-x-circle me-1"></i>
              Cancelar
            </button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  )
}
